import React, { useEffect, useMemo, useState } from 'react';
import { OperationLog } from '../types/OperationLog';

type Props = {
  logs?: OperationLog[];
  loading?: boolean;
  error?: string | null;
  /** When true (default), the component fetches by itself */
  autoFetch?: boolean;
  /** Override endpoint if needed (default: /api/operation-log/history) */
  fetchUrl?: string;
  onLogClick: (log: OperationLog) => void;
};

/* ======================= THEME (matches your dashboard) ======================= */
const COLORS = {
  bg: '#0f172a',
  panel: '#111827',
  panel2: '#0b1220',
  text: '#e5e7eb',
  sub: '#94a3b8',
  border: 'rgba(255,255,255,0.08)',
  accent: '#60a5fa',
  ok: '#34d399',
  danger: '#f87171',
  warn: '#fbbf24',
};

const wrapS: React.CSSProperties = {
  direction: 'rtl',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
  gap: 12,
  padding: '8px 12px 24px',
};

const cardS: React.CSSProperties = {
  background: COLORS.panel2,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 14,
  padding: 14,
  color: COLORS.text,
  boxShadow: '0 6px 16px rgba(0,0,0,.18)',
  transition: 'transform .15s ease, box-shadow .15s ease',
};
const cardHover: React.CSSProperties = {
  transform: 'translateY(-2px)',
  boxShadow: '0 10px 24px rgba(0,0,0,.28)',
};

const rowS: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
};

const titleS: React.CSSProperties = {
  fontWeight: 900,
  fontSize: 14,
};
const labelS: React.CSSProperties = {
  fontSize: 12,
  color: COLORS.sub,
};

const valueS: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 800,
  color: COLORS.text,
};

const badgeBase: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 10px',
  borderRadius: 999,
  border: `1px solid ${COLORS.border}`,
  fontSize: 12,
  fontWeight: 800,
  whiteSpace: 'nowrap',
};

const badge = {
  neutral: { ...badgeBase, background: 'rgba(148,163,184,0.12)', color: COLORS.text },
  success: { ...badgeBase, background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,.45)', color: COLORS.ok },
  danger:  { ...badgeBase, background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,.45)', color: COLORS.danger },
  warn:    { ...badgeBase, background: 'rgba(251,191,36,0.12)',  border: '1px solid rgba(251,191,36,.45)',  color: COLORS.warn },
};

const chip: React.CSSProperties = {
  ...badgeBase,
  padding: '2px 8px',
  background: 'rgba(96,165,250,0.10)',
  border: `1px solid ${COLORS.accent}`,
  color: COLORS.text,
  fontSize: 11.5,
};

const footerS: React.CSSProperties = {
  marginTop: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  color: COLORS.sub,
  fontSize: 12,
  borderTop: `1px dashed ${COLORS.border}`,
  paddingTop: 8,
};

/* ======================= label & class helpers (unchanged) ======================= */
// OperationType: 0=Install, 1=ForceInstall, 2=Remove
const TYPE_LABEL_NUM: Record<number, string> = { 0: 'نصب', 1: 'نصب (اجباری)', 2: 'برداشتن' };
const TYPE_LABEL_STR: Record<string, string> = {
  install: 'نصب',
  'force_install': 'نصب (اجباری)',
  forceinstall: 'نصب (اجباری)',
  remove: 'برداشتن',
};

// OperationStatus: 0=InProgress, 1=Rejected, 2=Approved
const STATUS_LABEL_NUM: Record<number, string> = { 0: 'در حال انجام', 1: 'رد شده', 2: 'تأیید شده' };
const STATUS_LABEL_STR: Record<string, string> = {
  in_progress: 'در حال انجام',
  processing: 'در حال انجام',
  pending: 'در حال انجام',
  rejected: 'رد شده',
  approved: 'تأیید شده',
  confirmed: 'تأیید شده',
  done: 'تأیید شده',
  completed: 'تأیید شده',
};

const isPersian = (s: string) => /[\u0600-\u06FF]/.test(s);
const toKey = (s: string) => s.toLowerCase().replace(/\s+/g, '_');

const resolveType = (log: any): { label: string; source: string; raw: unknown } => {
  const raw = log?.type ?? log?.Type ?? log?.operation_type ?? log?.operationType ?? log?.op_type ?? null;
  if (raw !== null && raw !== undefined) {
    if (typeof raw === 'number') return { label: TYPE_LABEL_NUM[raw] ?? 'نامشخص', source: 'type', raw };
    const s = String(raw).trim();
    if (isPersian(s)) return { label: s, source: 'type', raw };
    const k = toKey(s);
    return { label: TYPE_LABEL_STR[k] ?? 'نامشخص', source: 'type', raw };
  }
  for (const [k, v] of Object.entries(log || {})) {
    if (typeof v === 'string' && /نصب|برداشتن/.test(v)) return { label: v, source: k, raw: v };
  }
  return { label: 'نامشخص', source: '', raw: undefined };
};

const getStatusLabel = (log: any) => {
  const raw = log?.status ?? log?.Status ?? log?.operation_status ?? log?.operationStatus;
  if (typeof raw === 'number') return STATUS_LABEL_NUM[raw] ?? 'نامشخص';
  if (typeof raw === 'string') return STATUS_LABEL_STR[toKey(raw)] ?? raw;
  return 'نامشخص';
};

const getStatusStyle = (log: any): React.CSSProperties => {
  const raw = log?.status ?? log?.Status ?? log?.operation_status ?? log?.operationStatus ?? 0;
  if (raw === 2 || ['approved', 'confirmed', 'done', 'completed'].includes(String(raw).toLowerCase())) return badge.success;
  if (raw === 1 || String(raw).toLowerCase() === 'rejected') return badge.danger;
  return badge.neutral;
};

const getIsoPoint = (log: any) =>
  log?.isolation_point_id ?? log?.IsolationPointId ?? log?.isolationPointId ?? '—';

const getId = (log: any) => log?.id ?? log?.Id ?? '—';

const formatFaDate = (d?: string) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '—';
  const time = new Intl.DateTimeFormat('fa-IR', { hour: '2-digit', minute: '2-digit', hour12: false }).format(date);
  const ymd  = new Intl.DateTimeFormat('fa-IR', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
  return `${time} - ${ymd}`;
};

const getCreatedAt = (log: any) =>
  log?.created_at ?? log?.createdAt ?? log?.CreatedAt ?? log?.date ?? log?.created ?? undefined;

/* ======================= small wrappers ======================= */
const EmptyState = () => (
  <div style={{ color: COLORS.sub, textAlign: 'center', padding: '16px 0' }}>لاگی یافت نشد.</div>
);

const ErrorBanner: React.FC<{ message: string }> = ({ message }) => (
  <div
    style={{
      margin: '8px 12px',
      padding: '10px 12px',
      borderRadius: 10,
      background: 'rgba(248,113,113,0.12)',
      border: '1px solid rgba(248,113,113,.35)',
      color: COLORS.danger,
      fontWeight: 800,
    }}
  >
    خطا در دریافت اطلاعات: {message}
  </div>
);

/* ======================= Component ======================= */
const OperationLogList: React.FC<Props> = ({
  logs: externalLogs,
  loading: externalLoading,
  error: externalError,
  autoFetch = true,
  fetchUrl = '/api/operation-log/history',
  onLogClick,
}) => {
  const [internalLogs, setInternalLogs] = useState<OperationLog[]>([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const [internalError, setInternalError] = useState<string | null>(null);

  const useInternal = autoFetch && !externalLogs;
  const logs = useInternal ? internalLogs : (externalLogs || []);
  const loading = useInternal ? internalLoading : !!externalLoading;
  const error = useInternal ? internalError : externalError ?? null;

  useEffect(() => {
    if (!useInternal) return;
    const token = localStorage.getItem('token');

    const tryFetch = async (url: string) => {
      const res = await fetch(url, {
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
      });
      const text = await res.text();
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${text}`);
      const json = text ? JSON.parse(text) : [];
      const arr = Array.isArray(json)
        ? json
        : json?.operations ?? json?.data?.operations ?? json?.items ?? json?.data?.items ?? [];
      return (arr || []) as OperationLog[];
    };

    const load = async () => {
      setInternalLoading(true);
      setInternalError(null);
      try {
        const arr = await tryFetch(fetchUrl);
        setInternalLogs(arr);
      } catch (e: any) {
        setInternalLogs([]);
        setInternalError(e?.message || 'خطا در دریافت لاگ‌ها');
      } finally {
        setInternalLoading(false);
      }
    };

    load();
  }, [useInternal, fetchUrl]);

  const body = useMemo(() => {
    if (error) return <ErrorBanner message={error} />;
    if (loading) return <div style={{ ...cardS }}>در حال بارگذاری…</div>;
    if (!loading && logs.length === 0) return <EmptyState />;

    return (
      <div style={wrapS}>
        {logs.map((log: any) => {
          const id        = getId(log);
          const created   = formatFaDate(getCreatedAt(log));
          const { label: typeText } = resolveType(log);
          const statusTxt = getStatusLabel(log);
          const iso       = getIsoPoint(log);
          const statusStyle = getStatusStyle(log);

          return (
            <div
              key={id}
              style={cardS}
              onClick={() => onLogClick(log)}
              onMouseEnter={(e) => Object.assign((e.currentTarget as HTMLDivElement).style, cardHover)}
              onMouseLeave={(e) => Object.assign((e.currentTarget as HTMLDivElement).style, cardS)}
            >
              {/* Header line: type + status */}
              <div style={{ ...rowS, marginBottom: 10 }}>
                <div style={titleS}>گزارش عملیات</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <span style={chip}>{typeText}</span>
                  <span style={statusStyle}>{statusTxt}</span>
                </div>
              </div>

              {/* Body */}
              <div style={{ display: 'grid', gap: 6 }}>
                <div style={rowS}>
                  <span style={labelS}>نقطه ایزولاسیون</span>
                  <span style={valueS}>{iso}</span>
                </div>
              </div>

              {/* Footer */}
              <div style={footerS}>
                <span style={{ fontWeight: 800, color: COLORS.text }}>#{id}</span>
                <span>{created}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [logs, loading, error, onLogClick]);

  return <>{body}</>;
};

export default OperationLogList;
